import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { postAnswers } from 'api/answers/answers.api';
import {
  FormName,
  MunicipalPartner,
  PostAnswersPayload,
} from 'api/answers/dtos/answers.dto';
import { InstitutionForm } from 'components/cifa/institution_form';
import { ContainerForm } from 'components/shared/container_form';
import { FormButton, FormButtonType } from 'components/shared/form_button';
import { HorizontalStepper } from 'components/shared/horizontal_stepper';
import { PageTitle } from 'components/shared/page_title';
import { SnackbarFeedback } from 'components/shared/snackbar_feedback';
import {
  usePermissions,
  ModuleEnum,
  PermissionType,
} from 'contexts/auth/permission_context';
import { ROUTES } from 'routes/constants';
import ContainerPainel from 'components/container_painel';

export interface InstitutionI {
  id: number;
  name: string;
}

interface InstitutionData {
  id: number;
  name: string;
  data: MunicipalPartner;
}


interface InstitutionData extends InstitutionI {
  data: MunicipalPartner;
}

// Exported helpers for unit testing
export const getSavedPartnerData = (id: number): MunicipalPartner | null => {
  const saved = localStorage.getItem('aterMunicipalPartnerList');
  if (!saved) return null;
  try {
    const list = JSON.parse(saved);
    const match = list.find((item: any) => item?.id === id);
    return match?.data ?? null;
  } catch {
    return null;
  }
};

export const computeUpdatedMunicipalPartners = (
  prevPartners: InstitutionData[] | undefined,
  value: MunicipalPartner,
  institution: InstitutionI,
): InstitutionData[] | undefined => {
  if (!institution || institution.id == null) return prevPartners;

  const safePartners = (prevPartners || []).filter(
    (partner): partner is InstitutionData => partner != null && typeof partner.id === 'number',
  );

  const existingPartnerIndex = safePartners.findIndex((partner) => partner.id === institution.id);
  const previous = safePartners[existingPartnerIndex];

  const isSameData = JSON.stringify(previous?.data) === JSON.stringify(value);
  if (isSameData) return prevPartners;

  const isCompletelyEmpty =
    !value.numberOfBeneficiary &&
    (!value.partnershipDocument?.uuid || !value.partnershipDocument?.name) &&
    (!value.ataCmdrsDocument?.uuid || !value.ataCmdrsDocument?.name);

  if (isCompletelyEmpty) {
    console.warn('❌ Ignorando atualização com dados vazios para:', institution.name);
    return prevPartners;
  }

  const updatedData: InstitutionData = {
    id: institution.id,
    name: institution.name,
    data: {
      name: value.name || institution.name,
      numberOfBeneficiary: value.numberOfBeneficiary ?? previous?.data?.numberOfBeneficiary ?? 0,
      partnershipDocument: value.partnershipDocument?.uuid
        ? {
          uuid: value.partnershipDocument.uuid,
          name: value.partnershipDocument.name,
        }
        : previous?.data?.partnershipDocument ?? { uuid: '', name: '' },
      ataCmdrsDocument: value.ataCmdrsDocument?.uuid
        ? {
          uuid: value.ataCmdrsDocument.uuid,
          name: value.ataCmdrsDocument.name,
        }
        : previous?.data?.ataCmdrsDocument ?? { uuid: '', name: '' },
    },
  };

  let updatedPartners: InstitutionData[] = [];

  if (existingPartnerIndex >= 0) {
    updatedPartners = [...safePartners];
    updatedPartners[existingPartnerIndex] = updatedData;
  } else {
    updatedPartners = [...safePartners, updatedData];
  }

  return updatedPartners;
};

export const mapSavedPartners = (
  saved: string | null,
  institutions: InstitutionI[],
): InstitutionData[] => {
  if (!saved) return [];
  try {
    const parsed = JSON.parse(saved) as MunicipalPartner[];

    return parsed.map((partner) => {
      const matched = institutions.find((inst) =>
        inst.name.toLowerCase().includes(partner.name.toLowerCase()),
      );

      return {
        id: matched?.id ?? 0,
        name: matched?.name ?? '',
        data: partner,
      };
    });
  } catch (err) {
    console.error('Erro ao restaurar municipalPartners:', err);
    return [];
  }
};

const CreateCifa2025: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (!hasPermission(ModuleEnum.CIFA, PermissionType.CREATE)) {
      alert('Você não tem permissão para acessar esta página.');
      navigate(ROUTES.HOME);
    }
  }, []);


  useEffect(() => {
    const savedPartners = localStorage.getItem('aterMunicipalPartnerList');
    const savedInstitutions = localStorage.getItem('aterInstitutionsSelected');
    const savedMunicipality = localStorage.getItem('aterExclusiveServiceMunicipality');
    const savedPartnerInstitution = localStorage.getItem('aterServiceByMunicipalPartner');



    try {
      if (savedPartners) {
        try {
          const parsed = JSON.parse(savedPartners) as InstitutionData[];
          setMunicipalPartners(parsed);
        } catch (err) {
          console.error('Erro ao restaurar municipalPartners:', err);
        }
      }
      if (savedInstitutions) {
        setInstitutions(JSON.parse(savedInstitutions));
      }
      if (savedMunicipality !== null) {
        setAterExclusiveServiceMunicipality(savedMunicipality);
      }
      if (savedPartnerInstitution !== null) {
        setPartnerInstitution(savedPartnerInstitution === 'true' ? '1' : '0');
      }
    } catch (e) {
      console.error('Erro ao restaurar dados salvos:', e);
    }
  }, []);





  const [institutions, setInstitutions] = useState<InstitutionI[]>(() => {
    const saved = localStorage.getItem('aterInstitutionsSelected');
    return saved ? JSON.parse(saved) : [];
  });
  const [aterExclusiveServiceMunicipality, setAterExclusiveServiceMunicipality] = useState<string>(() => {
    return localStorage.getItem('aterExclusiveServiceMunicipality') ?? '';
  });
  const [partnerInstitution, setPartnerInstitution] = useState<'0' | '1'>(() => {
    const saved = localStorage.getItem('aterServiceByMunicipalPartner');
    return saved === 'true' ? '1' : '0';
  });

  const [municipalPartners, setMunicipalPartners] = useState<InstitutionData[]>(() => {
    const saved = localStorage.getItem('aterMunicipalPartnerList');
    return mapSavedPartners(saved, institutions);
  });



  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState<boolean>();

  const handleInstitution = (id: number, name: string) => {
    setInstitutions((prev) => {
      const exists = prev.some((inst) => inst.id === id);
      let updated: InstitutionI[];
      if (exists) {
        updated = prev.filter((inst) => inst.id !== id);
      } else {
        updated = [...prev, { id, name }];
      }

      localStorage.setItem('aterInstitutionsSelected', JSON.stringify(updated));

      if (updated.length === 0) {
        localStorage.removeItem('aterMunicipalPartnerList');
        setMunicipalPartners([]);
      }

      return updated;
    });
  };

  const updateMunicipalPartners = (
    value: MunicipalPartner,
    institution: InstitutionI,
  ) => {
    if (!institution || institution.id == null) return;
    setMunicipalPartners((prevPartners) => {
      const updated = computeUpdatedMunicipalPartners(prevPartners, value, institution);
      if (updated) {
        localStorage.setItem('aterMunicipalPartnerList', JSON.stringify(updated));
      }
      return updated ?? prevPartners;
    });
  };








  const handleSubmit = async () => {
    setLoading(true);

    const userAccessUuid = localStorage.getItem('userAccessUuid') ?? '';
    const institutionUuid = localStorage.getItem('institutionUuid') ?? '';

    // Prepara apenas os dados que vão no payload
    const municipalPartnersData = municipalPartners
      ?.map((partner) => partner?.data)
      .filter((data) => data !== null && data !== undefined) || [];

    const payload: PostAnswersPayload = {
      userAccessUuid,
      institutionUuid,
      formName: FormName.ASSISTENCIA_TECNICA_E_EXTENSAO_RURAL,
      answer: {
        aterExclusiveServiceMunicipality: Number(aterExclusiveServiceMunicipality || 0),
        aterServiceByMunicipalPartner: partnerInstitution === '1',
        aterMunicipalPartnerList: municipalPartnersData,
      },
      referencedYear: 2025,
    };

    // Salva dados persistentes corretamente
    localStorage.setItem(
      'aterExclusiveServiceMunicipality',
      String(aterExclusiveServiceMunicipality || 0)
    );
    localStorage.setItem(
      'aterServiceByMunicipalPartner',
      partnerInstitution === '1' ? 'true' : 'false'
    );
    localStorage.setItem(
      'aterMunicipalPartnerList',
      JSON.stringify(municipalPartners) // sempre salva o completo
    );

    try {
      const response = await postAnswers(payload);

      if (response.dados.statusCode === 401) {
        setErrorMessage(response.dados.message);
        return;
      }

      // Navega para próxima etapa
      navigate(ROUTES.CREATE_FEDERAL_SCHOOL_FEEDING_2025);
    } catch (error) {
      setErrorMessage(
        'Erro ao enviar os dados da Assistência Técnica e Extensão Rural.'
      );
    } finally {
      setLoading(false);
    }
  };



  return (
    <ContainerPainel
      pagina='CIFA - Cadastrar Município - 2025'
      showBackButton={true}
      backButtonPath={ROUTES.CIFA2025}
    >
      <HorizontalStepper activeStep={0} />

      <PageTitle
        title='Assistência Técnica e Extensão Rural'
        subtitle='Informe corretamente os dados sobre os atendimentos ATER realizados no Município.'
      />

      <Stack marginX='4rem'>
        <Alert
          severity='info'
          sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
        >
          <Typography
            fontSize='14px'
            variant='body2'
            textAlign='left'
            lineHeight={1.5}
          >
            Considera-se Assistência Técnica e Extensão Rural - ATER o número de
            atendimentos prestados aos beneficiários da agricultura familiar
            (unidade agro familiar) do município por profissional de ATER, por
            meio de, no mínimo, 1 (uma) visita presencial ao longo do ano.
          </Typography>
        </Alert>

        <ContainerForm title='ATER - Atendidos exclusivamente pelo Município'>
          <TextField
            id='beneficiarios-exclusivamente-municipio'
            label='Quantos beneficiários da Agricultura Familiar [...]'
            variant='outlined'
            value={aterExclusiveServiceMunicipality}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setAterExclusiveServiceMunicipality(value);
                localStorage.setItem('aterExclusiveServiceMunicipality', value);
              }
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 100 }}
          />
        </ContainerForm>

        <ContainerForm title='ATER - Atendidos por Instituições parceiras do Município'>
          <FormControl>
            <FormLabel id='foreigner-label'>
              O Município realizou parceria com uma instituição executora de
              Assistência Técnica e Extensão Rural - ATER em 2024? (Opcional)
            </FormLabel>

            <RadioGroup
              value={partnerInstitution}
              onChange={(e) => {
                const value = e.target.value as '0' | '1';
                setPartnerInstitution(value);
                localStorage.setItem('aterServiceByMunicipalPartner', value); // <- salvar aqui

                if (value === '0') {
                  setInstitutions([]);
                  localStorage.removeItem('aterInstitutionsSelected');
                  localStorage.removeItem('aterMunicipalPartnerList');
                }
              }}

            >
              <FormControlLabel value="0" control={<Radio />} label="Não" />
              <FormControlLabel value="1" control={<Radio />} label="Sim" />
            </RadioGroup>

          </FormControl>

          {partnerInstitution === '1' && (
            <FormGroup>
              <FormLabel id='foreigner-label'>
                Informe a(s) Instituição(ões) em que foi realizada parceria:
              </FormLabel>

              <FormControlLabel
                label='EMPAER MT'
                control={
                  <Checkbox
                    checked={institutions
                      .flatMap((institution) => institution.id)
                      .includes(1)}
                    onChange={() => handleInstitution(1, 'EMPAER MT')}
                  />
                }
              />
              <FormControlLabel
                label='Instituições de Ensino'
                control={
                  <Checkbox
                    checked={institutions
                      .flatMap((institution) => institution.id)
                      .includes(2)}
                    onChange={() =>
                      handleInstitution(2, 'Instituições de Ensino')
                    }
                  />
                }
              />
              <FormControlLabel
                label='Organizações - ONGs'
                control={
                  <Checkbox
                    checked={institutions
                      .flatMap((institution) => institution.id)
                      .includes(3)}
                    onChange={() => handleInstitution(3, 'Organizações - ONGs')}
                  />
                }
              />
              <FormControlLabel
                label='Instituições Privadas/Outros'
                control={
                  <Checkbox
                    checked={institutions
                      .flatMap((institution) => institution.id)
                      .includes(4)}
                    onChange={() =>
                      handleInstitution(4, 'Instituições Privadas/Outros')
                    }
                  />
                }
              />
            </FormGroup>
          )}
        </ContainerForm>

        {institutions.map((institution) => {
          const matchedInstitution = municipalPartners.find(p => p.id === institution.id);

          return (
            <InstitutionForm
              key={institution.id}
              institution={institution}
              data={matchedInstitution?.data}
              setInstituion={(value) => updateMunicipalPartners(value, institution)}
            />
          );
        })}






        <Stack
          direction={{ md: 'row-reverse' }}
          marginY='2rem'
          sx={{ gap: '1rem' }}
        >
          <FormButton
            type={FormButtonType.PRIMARY}
            title='Avançar'
            onClick={handleSubmit}
            isDisabled={loading}
          />
          <FormButton
            type={FormButtonType.SECONDARY}
            title='Voltar'
            onClick={() => navigate(ROUTES.CIFA2025)}
          />
        </Stack>

        {errorMessage && (
          <SnackbarFeedback isOpen type='error' message={errorMessage} />
        )}
      </Stack>
    </ContainerPainel>
  );
};

export default CreateCifa2025;
